"use client"

import * as React from 'react'
import type { TranslateFn } from '@open-mercato/shared/lib/i18n/context'
import type { FilterDef } from '@open-mercato/ui/backend/FilterBar'
import type { DynamicTableColumn, DynamicTableOption } from '@open-mercato/ui/backend/DynamicTable'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Switch } from '@open-mercato/ui/primitives/switch'
import {
  EURO_CLASSES,
  FLEET_CAPABILITIES,
  GPS_PROVIDERS,
  FUEL_TYPES,
  VEHICLE_TYPES,
  type FleetCapability,
  type VehicleEuroClass,
  type VehicleFuelType,
  type VehicleGpsProvider,
  type VehicleType,
} from './capabilities'

export type FleetTranslate = TranslateFn

export type FleetVehicleListItem = {
  id: string
  registrationPlate: string
  type: VehicleType
  brand: string | null
  model: string | null
  year: number | null
  capabilities: FleetCapability[]
  euroClass: VehicleEuroClass | null
  maxPayloadKg: number | null
  gpsProvider: VehicleGpsProvider | null
  isActive: boolean
  insuranceExpires: string | null
  inspectionExpires: string | null
}

export type FleetVehicleDetail = {
  id: string
  registrationPlate: string
  type: VehicleType
  capabilities: FleetCapability[]
  brand: string | null
  model: string | null
  year: number | null
  tareWeightKg: number | null
  maxPayloadKg: number | null
  euroClass: VehicleEuroClass | null
  fuelType: VehicleFuelType | null
  insuranceExpires: string | null
  inspectionExpires: string | null
  insuranceStatus: 'valid' | 'expired' | 'missing'
  inspectionStatus: 'valid' | 'expired' | 'missing'
  insuranceDaysUntil: number | null
  inspectionDaysUntil: number | null
  gpsDeviceId: string | null
  gpsProvider: VehicleGpsProvider | null
  specs: Record<string, unknown> | null
  isActive: boolean
  maintenance: {
    implemented: false
    items: unknown[]
  }
  assignmentHistory: {
    implemented: false
    items: unknown[]
  }
  createdAt: string
  updatedAt: string
}

export type FleetListResponse = {
  items: FleetVehicleListItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

function toOption(value: string, label: string, description?: string | null): DynamicTableOption {
  return { value, label, description: description ?? null }
}

export function formatVehicleTypeLabel(t: FleetTranslate, value: VehicleType | string | null | undefined): string {
  switch (value) {
    case 'TRUCK':
      return t('fleet.lookups.vehicleType.TRUCK', 'Truck')
    case 'TRAILER':
      return t('fleet.lookups.vehicleType.TRAILER', 'Trailer')
    case 'SOLO':
      return t('fleet.lookups.vehicleType.SOLO', 'Solo')
    default:
      return value ?? '—'
  }
}

export function formatCapabilityLabel(t: FleetTranslate, value: FleetCapability | string | null | undefined): string {
  switch (value) {
    case 'CONTAINER':
      return t('fleet.lookups.capability.CONTAINER', 'Container')
    case 'FTL':
      return t('fleet.lookups.capability.FTL', 'FTL')
    case 'REEFER':
      return t('fleet.lookups.capability.REEFER', 'Reefer')
    case 'ADR':
      return t('fleet.lookups.capability.ADR', 'ADR')
    case 'OVERSIZED':
      return t('fleet.lookups.capability.OVERSIZED', 'Oversized')
    case 'BULK':
      return t('fleet.lookups.capability.BULK', 'Bulk')
    default:
      return value ?? '—'
  }
}

export function formatEuroClassLabel(t: FleetTranslate, value: VehicleEuroClass | string | null | undefined): string {
  switch (value) {
    case 'EURO3':
      return t('fleet.lookups.euroClass.EURO3', 'Euro 3')
    case 'EURO4':
      return t('fleet.lookups.euroClass.EURO4', 'Euro 4')
    case 'EURO5':
      return t('fleet.lookups.euroClass.EURO5', 'Euro 5')
    case 'EURO6':
      return t('fleet.lookups.euroClass.EURO6', 'Euro 6')
    case 'EURO6D':
      return t('fleet.lookups.euroClass.EURO6D', 'Euro 6d')
    default:
      return value ?? '—'
  }
}

export function formatFuelTypeLabel(t: FleetTranslate, value: VehicleFuelType | string | null | undefined): string {
  switch (value) {
    case 'DIESEL':
      return t('fleet.lookups.fuelType.DIESEL', 'Diesel')
    case 'LNG':
      return t('fleet.lookups.fuelType.LNG', 'LNG')
    case 'CNG':
      return t('fleet.lookups.fuelType.CNG', 'CNG')
    case 'ELECTRIC':
      return t('fleet.lookups.fuelType.ELECTRIC', 'Electric')
    case 'HYBRID':
      return t('fleet.lookups.fuelType.HYBRID', 'Hybrid')
    default:
      return value ?? '—'
  }
}

export function formatGpsProviderLabel(t: FleetTranslate, value: VehicleGpsProvider | string | null | undefined): string {
  switch (value) {
    case 'GBOX':
      return t('fleet.lookups.gpsProvider.GBOX', 'GBOX')
    case 'TELTONIKA':
      return t('fleet.lookups.gpsProvider.TELTONIKA', 'Teltonika')
    case 'MANUAL':
      return t('fleet.lookups.gpsProvider.MANUAL', 'Manual')
    case 'PWA':
      return t('fleet.lookups.gpsProvider.PWA', 'PWA')
    default:
      return value ?? '—'
  }
}

export function getVehicleTypeOptions(t: FleetTranslate): DynamicTableOption[] {
  return VEHICLE_TYPES.map((value) => toOption(value, formatVehicleTypeLabel(t, value)))
}

export function getCapabilityOptions(t: FleetTranslate): DynamicTableOption[] {
  return FLEET_CAPABILITIES.map((value) => toOption(value, formatCapabilityLabel(t, value)))
}

export function getEuroClassOptions(t: FleetTranslate): DynamicTableOption[] {
  return EURO_CLASSES.map((value) => toOption(value, formatEuroClassLabel(t, value)))
}

export function getFuelTypeOptions(t: FleetTranslate): DynamicTableOption[] {
  return FUEL_TYPES.map((value) => toOption(value, formatFuelTypeLabel(t, value)))
}

export function getGpsProviderOptions(t: FleetTranslate): DynamicTableOption[] {
  return GPS_PROVIDERS.map((value) => toOption(value, formatGpsProviderLabel(t, value)))
}

function formatPayload(value: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value % 1 === 0 ? 0 : 3,
  }).format(value)
}

function renderCapabilities(t: FleetTranslate, capabilities: readonly string[]) {
  if (!capabilities.length) {
    return <span className="text-muted-foreground">—</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {capabilities.map((capability) => (
        <Badge key={capability} variant="outline" className="bg-muted/30">
          {formatCapabilityLabel(t, capability)}
        </Badge>
      ))}
    </div>
  )
}

export function buildFleetFilters(t: FleetTranslate): FilterDef[] {
  return [
    {
      id: 'type',
      label: t('fleet.table.filters.type', 'Type'),
      type: 'select',
      options: getVehicleTypeOptions(t),
    },
    {
      id: 'capability',
      label: t('fleet.table.filters.capability', 'Capability'),
      type: 'tags',
      options: getCapabilityOptions(t),
      placeholder: t('fleet.table.filters.capabilityPlaceholder', 'Filter by capability'),
      formatValue: (value) => formatCapabilityLabel(t, value),
    },
    {
      id: 'euroClass',
      label: t('fleet.table.filters.euroClass', 'Euro class'),
      type: 'select',
      options: getEuroClassOptions(t),
    },
    {
      id: 'gpsProvider',
      label: t('fleet.table.filters.gpsProvider', 'GPS provider'),
      type: 'select',
      options: getGpsProviderOptions(t),
    },
    {
      id: 'isActive',
      label: t('fleet.table.filters.active', 'Active'),
      type: 'checkbox',
    },
  ]
}

export function buildFleetColumns(
  t: FleetTranslate,
  options: {
    onToggleActive: (row: FleetVehicleListItem, next: boolean) => void
    activeToggleDisabled?: (row: FleetVehicleListItem) => boolean
  },
): DynamicTableColumn<FleetVehicleListItem>[] {
  return [
    {
      accessorKey: 'registrationPlate',
      header: t('fleet.table.columns.registrationPlate', 'Reg. plate'),
      editor: {
        type: 'text',
        placeholder: t('fleet.form.placeholders.registrationPlate', 'Enter registration plate'),
      },
      meta: { truncate: true, maxWidth: 160 },
    },
    {
      accessorKey: 'type',
      header: t('fleet.table.columns.type', 'Type'),
      cell: ({ row }) => formatVehicleTypeLabel(t, row.original.type),
      editor: {
        type: 'select',
        options: getVehicleTypeOptions(t),
      },
      meta: { truncate: true, maxWidth: 120 },
    },
    {
      accessorKey: 'brand',
      header: t('fleet.table.columns.brand', 'Brand'),
      editor: { type: 'text' },
      meta: { truncate: true, maxWidth: 140 },
    },
    {
      accessorKey: 'model',
      header: t('fleet.table.columns.model', 'Model'),
      editor: { type: 'text' },
      meta: { truncate: true, maxWidth: 140 },
    },
    {
      accessorKey: 'year',
      header: t('fleet.table.columns.year', 'Year'),
      editor: { type: 'number', min: 1980, step: 1 },
      meta: { truncate: true, maxWidth: 90 },
    },
    {
      accessorKey: 'capabilities',
      header: t('fleet.table.columns.capabilities', 'Capabilities'),
      cell: ({ row }) => renderCapabilities(t, row.original.capabilities),
      editor: {
        type: 'tags',
        suggestions: getCapabilityOptions(t),
        allowCustomValues: false,
      },
      meta: { maxWidth: 280 },
    },
    {
      accessorKey: 'euroClass',
      header: t('fleet.table.columns.euroClass', 'Euro class'),
      cell: ({ row }) => formatEuroClassLabel(t, row.original.euroClass),
      editor: {
        type: 'select',
        options: getEuroClassOptions(t),
        disabled: ({ draft }) => draft.type === 'TRAILER',
      },
      meta: { truncate: true, maxWidth: 120 },
    },
    {
      accessorKey: 'maxPayloadKg',
      header: t('fleet.table.columns.maxPayloadKg', 'Payload (kg)'),
      cell: ({ row }) => formatPayload(row.original.maxPayloadKg),
      editor: { type: 'number', min: 0, step: 'any' },
      meta: { truncate: true, maxWidth: 140 },
    },
    {
      accessorKey: 'gpsProvider',
      header: t('fleet.table.columns.gpsProvider', 'GPS provider'),
      cell: ({ row }) => formatGpsProviderLabel(t, row.original.gpsProvider),
      editor: {
        type: 'select',
        options: getGpsProviderOptions(t),
      },
      meta: { truncate: true, maxWidth: 140 },
    },
    {
      accessorKey: 'isActive',
      header: t('fleet.table.columns.isActive', 'Active'),
      cell: ({ row }) => (
        <div
          className="flex items-center"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Switch
            checked={row.original.isActive}
            onCheckedChange={(checked) => options.onToggleActive(row.original, checked)}
            disabled={options.activeToggleDisabled?.(row.original)}
          />
        </div>
      ),
      editor: { type: 'checkbox' },
      meta: { truncate: true, maxWidth: 90 },
    },
  ]
}
